import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import MainLayout from "../layout/MainLayout";

// Import interior images dynamically (e.g., /assets/premium/)
const interiorImages = Array.from({ length: 15 }, (_, i) => {
  try {
    return require(`../assets/premium/interior-${i + 1}.jpeg`);
  } catch (err) {
    console.warn(`Interior image not found: interior-${i + 1}.jpeg`);
    return null;
  }
}).filter(Boolean);

export const PremiumComfort = () => {
  const [selectedImg, setSelectedImg] = useState(null);

  return (
    <MainLayout>
      {/* Hero / Header */}
      <section className="text-center py-16 px-4 max-w-4xl mx-auto">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-4xl md:text-5xl font-bold mb-4 just-me-again-down-here-regular"
        >
          Premium Comfort & Interior Experience
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto"
        >
          Step inside our premium vehicles and discover a world of comfort,
          style, and sophistication. Every detail is designed to give you a
          smooth, luxurious ride — whether for business, leisure, or safari.
        </motion.p>
      </section>

      {/* Gallery Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-20 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {interiorImages.map((img, index) => (
          <motion.div
            key={index}
            className="relative overflow-hidden rounded-xl cursor-pointer group"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            whileHover={{ scale: 1.03 }}
            onClick={() => setSelectedImg(img)}
          >
            <img
              src={img}
              alt={`Premium Interior ${index + 1}`}
              className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-lg font-medium tracking-wide">
              View Interior
            </div>
          </motion.div>
        ))}
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImg && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 backdrop-blur-sm p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedImg(null)}
          >
            <motion.div
              className="relative max-w-5xl w-full max-h-[90vh]"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedImg(null)}
                className="absolute top-4 right-4 bg-black/60 p-2 rounded-full text-white hover:bg-black transition"
              >
                <X size={24} />
              </button>
              <img
                src={selectedImg}
                alt="Premium Interior"
                className="rounded-xl w-full h-auto object-contain shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* CTA Section */}
      <section className="text-center py-16 px-4 bg-gradient-to-br from-teal-700 to-teal-900 text-white rounded-3xl mx-4 sm:mx-10 my-10 shadow-2xl">
        <motion.h3
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-4xl font-bold mb-4 just-me-again-down-here-regular"
        >
          Comfort that Moves with You
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="max-w-3xl mx-auto text-white/90 text-lg mb-6"
        >
          From plush seats to climate control, your journey should be as
          comfortable as your destination is exciting. Discover our premium
          fleet for your next ride.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <a
            href="/contact"
            className="inline-block px-6 py-3 bg-white text-teal-700 font-semibold rounded-lg hover:bg-gray-100 transition"
          >
            Book a Premium Ride
          </a>
        </motion.div>
      </section>
    </MainLayout>
  );
};

